import { useState, useEffect, useCallback } from 'react';
import { hrAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Loader2, GraduationCap, CheckCircle2, Circle } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateShort, STATUS_STYLES } from './hrUtils';

export const OnboardingTab = () => {
  const [tasks, setTasks] = useState(null);

  const load = useCallback(() => {
    hrAPI.listTasks().then((res) => setTasks(res.data)).catch(() => setTasks([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const complete = async (id) => {
    try {
      await hrAPI.completeTask(id);
      toast.success('Task marked complete');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete task');
    }
  };

  if (!tasks) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;

  const done = tasks.filter((t) => t.status === 'completed').length;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="hr-onboarding-tab">
      <Card className="bg-white border-slate-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-slate-800 flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-cyan-500" /> Onboarding & Training Progress
            </p>
            <p className="text-sm text-slate-500" data-testid="hr-tasks-progress">{done} / {tasks.length} complete</p>
          </div>
          <Progress value={pct} className="h-2" />
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200">
        <CardHeader><CardTitle className="text-slate-800 text-lg">My Checklist</CardTitle></CardHeader>
        <CardContent className="space-y-2" data-testid="hr-tasks-list">
          {tasks.length === 0 && <p className="text-sm text-slate-400 py-4">No onboarding or training tasks assigned. You're all set!</p>}
          {tasks.map((t) => (
            <div key={t.id} className="flex items-start justify-between p-3 rounded-xl border border-slate-200 gap-3">
              <div className="flex items-start gap-3 min-w-0">
                {t.status === 'completed'
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                  : <Circle className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm font-medium ${t.status === 'completed' ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{t.title}</p>
                    <Badge variant="secondary" className="capitalize">{t.kind}</Badge>
                    <Badge className={STATUS_STYLES[t.status] || ''}>{t.status}</Badge>
                  </div>
                  {t.description && <p className="text-xs text-slate-500 mt-0.5">{t.description}</p>}
                  <p className="text-xs text-slate-400 mt-0.5">
                    {t.due_date ? `Due ${formatDateShort(t.due_date)} · ` : ''}Assigned by {t.assigned_by}
                  </p>
                </div>
              </div>
              {t.status !== 'completed' && (
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 rounded-full shrink-0" onClick={() => complete(t.id)} data-testid={`hr-task-complete-${t.id}`}>
                  Mark Complete
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};
